import {Exclude, Expose, instanceToPlain, plainToInstance, Type} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {SortFieldSpec} from "./search/sort-field-spec";
import {PageSpec} from "./search/page-spec";
import {FieldMatch, MatchCombine} from "./search/search";
import {DVIRCustomFieldValue, DVIRCustomFieldValueCodec, DVIRCustomFieldValueEntity} from "./dvir-custom-field-value";

export const DVIRCodec = t.intersection([
  t.type({
    id: t.string,
    regionId: t.string,
    personId: t.string,
    vehicleAirCompressor: t.boolean,
    vehicleAirLines: t.boolean,
    vehicleBattery: t.boolean,
    vehicleBrakes: t.boolean,
    vehicleBrakeAccessories: t.boolean,
    vehicleCarburetor: t.boolean,
    vehicleClutch: t.boolean,
    vehicleDefroster: t.boolean,
    vehicleDriveLine: t.boolean,
    vehicleEngine: t.boolean,
    vehicleFifthWheel: t.boolean,
    vehicleFrontAxle: t.boolean,
    vehicleFuelTanks: t.boolean,
    vehicleHeater: t.boolean,
    vehicleHorn: t.boolean,
    vehicleLights: t.boolean,
    vehicleMirrors: t.boolean,
    vehicleMuffler: t.boolean,
    vehicleOilPressure: t.boolean,
    vehicleOnBoardRecorder: t.boolean,
    vehicleRadiator: t.boolean,
    vehicleRearEnd: t.boolean,
    vehicleReflectors: t.boolean,
    vehicleSafetyEquipment: t.boolean,
    vehicleSprings: t.boolean,
    vehicleStarter: t.boolean,
    vehicleSteering: t.boolean,
    vehicleTachograph: t.boolean,
    vehicleTires: t.boolean,
    vehicleTransmission: t.boolean,
    vehicleWheels: t.boolean,
    vehicleWindows: t.boolean,
    vehicleWindshieldWipers: t.boolean,
    vehicleOther: t.boolean,
    trailerBrakeConnections: t.boolean,
    trailerBrakes: t.boolean,
    trailerCouplingChains: t.boolean,
    trailerCouplingPin: t.boolean,
    trailerDoors: t.boolean,
    trailerHitch: t.boolean,
    trailerLandingGear: t.boolean,
    trailerLights: t.boolean,
    trailerRoof: t.boolean,
    trailerSprings: t.boolean,
    trailerTarpaulin: t.boolean,
    trailerTires: t.boolean,
    trailerWheels: t.boolean,
    trailerOther: t.boolean,
    satisfactory: t.boolean,
    dttm: t.number,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    customFieldProfileId: t.string,
    customFieldValues: t.array(DVIRCustomFieldValueCodec),
    personSignatureId: t.string,
    personSignaturePath: t.string,
    personSignatureUrl: t.string,
    personSignatureStorageType: t.string,
    personSignatureDttm: t.number,
    person2Id: t.string,
    person2SignaturePath: t.string,
    person2SignatureUrl: t.string,
    person2SignatureStorageType: t.string,
    person2SignatureDttm: t.number,
    assignedMechanicId: t.string,
    mechanicId: t.string,
    mechanicName: t.string,
    mechanicSignaturePath: t.string,
    mechanicSignatureUrl: t.string,
    mechanicSignatureStorageType: t.string,
    mechanicSignatureDttm: t.number,
    vehicleId: t.string,
    trailerId: t.string,
    shiftId: t.string,
    comments: t.string,
    defectsCorrected: t.boolean,
    defectsNeedNotCorrected: t.boolean,
    defectsComments: t.string,
    deviceId: t.string,
    regionIds: t.array(t.string),
    createdById: t.string,
    createdByPersonName: t.string,
    lastModifiedById: t.string,
    lastModifiedByPersonName: t.string,
    personName: t.string,
    personSignatureName: t.string,
    person2Name: t.string,
    assignedMechanicName: t.string,
    mechanicPersonName: t.string,
    vehicleName: t.string,
    trailerName: t.string,

    personSignatureBase64: t.string,
    personSignatureMimeType: t.string,
    personSignature2Base64: t.string,
    personSignature2MimeType: t.string,
    mechanicSignatureBase64: t.string,
    mechanicSignatureMimeType: t.string,
  }),
]);

export const DVIRArrayCodec = t.array(
  DVIRCodec
);

export type DVIR = t.TypeOf<typeof DVIRCodec>;

type InspectionFieldKey = typeof DVIREntity.VEHICLE_INSPECTION_FIELDS[number]
  | typeof DVIREntity.TRAILER_INSPECTION_FIELDS[number];

export enum DVIRCustomFieldBucket {
  VEHICLE = "VEHICLE",
  TRAILER = "TRAILER",
  BOTH = "BOTH",
  UNKNOWN = "UNKNOWN",
}

const normalizeDefCode = (code?: string | null): string => {
  if (!code) {
    return "";
  }

  return code
    .trim()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .toUpperCase();
}

export const classifyDVIRCustomFieldBucket = (defCode?: string | null): DVIRCustomFieldBucket => {
  const s = normalizeDefCode(defCode);
  if (!s) {
    return DVIRCustomFieldBucket.UNKNOWN;
  }

  const hasVehicle =
    s === "VEHICLE" || s.startsWith("VEHICLE_") || s.includes("_VEHICLE_");
  const hasTrailer =
    s === "TRAILER" || s.startsWith("TRAILER_") || s.includes("_TRAILER_");

  if (hasVehicle && hasTrailer) {
    return DVIRCustomFieldBucket.BOTH;
  }
  if (hasVehicle) {
    return DVIRCustomFieldBucket.VEHICLE;
  }
  if (hasTrailer) {
    return DVIRCustomFieldBucket.TRAILER;
  }
  return DVIRCustomFieldBucket.UNKNOWN;
}

export const matchesDVIRCustomFieldBucket = (bucket: DVIRCustomFieldBucket, defCode?: string | null): boolean => {
  const b = classifyDVIRCustomFieldBucket(defCode);
  switch (bucket) {
    case DVIRCustomFieldBucket.VEHICLE:
      return b === DVIRCustomFieldBucket.VEHICLE || b === DVIRCustomFieldBucket.BOTH;
    case DVIRCustomFieldBucket.TRAILER:
      return b === DVIRCustomFieldBucket.TRAILER || b === DVIRCustomFieldBucket.BOTH;
    case DVIRCustomFieldBucket.UNKNOWN:
      return b === DVIRCustomFieldBucket.UNKNOWN;
    case DVIRCustomFieldBucket.BOTH:
      return b === DVIRCustomFieldBucket.BOTH;
  }
}

@Exclude()
export class DVIREntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  regionId!: string;

  @Expose()
  customFieldProfileId?: string;

  @Expose()
  @Type(() => DVIRCustomFieldValueEntity)
  customFieldValues?: DVIRCustomFieldValueEntity[];

  @Expose()
  personId!: string;

  @Expose()
  personName?: string;

  @Expose()
  personSignatureId?: string;

  @Expose()
  personSignatureName?: string;

  @Expose()
  personSignaturePath?: string;

  @Expose()
  personSignatureUrl?: string;

  @Expose()
  personSignatureStorageType?: string;

  @Expose()
  personSignatureDttm?: number;

  @Expose()
  person2Id!: string;

  @Expose()
  person2Name?: string;

  @Expose()
  person2SignaturePath?: string;

  @Expose()
  person2SignatureUrl?: string;

  @Expose()
  person2SignatureStorageType?: string;

  @Expose()
  person2SignatureDttm?: number;

  @Expose()
  assignedMechanicId?: string;

  @Expose()
  assignedMechanicName?: string;

  @Expose()
  mechanicId!: string;

  @Expose()
  mechanicName?: string;

  @Expose()
  mechanicPersonName?: string;

  @Expose()
  mechanicSignaturePath?: string;

  @Expose()
  mechanicSignatureUrl?: string;

  @Expose()
  mechanicSignatureStorageType?: string;

  @Expose()
  mechanicSignatureDttm?: number;

  @Expose()
  vehicleId?: string;

  @Expose()
  trailerId?: string;

  @Expose()
  shiftId?: string;

  @Expose()
  vehicleAirCompressor!: boolean;

  @Expose()
  vehicleAirLines!: boolean;

  @Expose()
  vehicleBattery!: boolean;

  @Expose()
  vehicleBrakes!: boolean;

  @Expose()
  vehicleBrakeAccessories!: boolean;

  @Expose()
  vehicleCarburetor!: boolean;

  @Expose()
  vehicleClutch!: boolean;

  @Expose()
  vehicleDefroster!: boolean;

  @Expose()
  vehicleDriveLine!: boolean;

  @Expose()
  vehicleEngine!: boolean;

  @Expose()
  vehicleFifthWheel!: boolean;

  @Expose()
  vehicleFrontAxle!: boolean;

  @Expose()
  vehicleFuelTanks!: boolean;

  @Expose()
  vehicleHeater!: boolean;

  @Expose()
  vehicleHorn!: boolean;

  @Expose()
  vehicleLights!: boolean;

  @Expose()
  vehicleMirrors!: boolean;

  @Expose()
  vehicleMuffler!: boolean;

  @Expose()
  vehicleOilPressure!: boolean;

  @Expose()
  vehicleOnBoardRecorder!: boolean;

  @Expose()
  vehicleRadiator!: boolean;

  @Expose()
  vehicleRearEnd!: boolean;

  @Expose()
  vehicleReflectors!: boolean;

  @Expose()
  vehicleSafetyEquipment!: boolean;

  @Expose()
  vehicleSprings!: boolean;

  @Expose()
  vehicleStarter!: boolean;

  @Expose()
  vehicleSteering!: boolean;

  @Expose()
  vehicleTachograph!: boolean;

  @Expose()
  vehicleTires!: boolean;

  @Expose()
  vehicleTransmission!: boolean;

  @Expose()
  vehicleWheels!: boolean;

  @Expose()
  vehicleWindows!: boolean;

  @Expose()
  vehicleWindshieldWipers!: boolean;

  @Expose()
  vehicleOther!: boolean;

  @Expose()
  trailerBrakeConnections!: boolean;

  @Expose()
  trailerBrakes!: boolean;

  @Expose()
  trailerCouplingChains!: boolean;

  @Expose()
  trailerCouplingPin!: boolean;

  @Expose()
  trailerDoors!: boolean;

  @Expose()
  trailerHitch!: boolean;

  @Expose()
  trailerLandingGear!: boolean;

  @Expose()
  trailerLights!: boolean;

  @Expose()
  trailerRoof!: boolean;

  @Expose()
  trailerSprings!: boolean;

  @Expose()
  trailerTarpaulin!: boolean;

  @Expose()
  trailerTires!: boolean;

  @Expose()
  trailerWheels!: boolean;

  @Expose()
  trailerOther!: boolean;

  @Expose()
  satisfactory!: boolean;

  @Expose()
  comments?: string;

  @Expose()
  defectsCorrected?: boolean;

  @Expose()
  defectsNeedNotCorrected?: boolean;

  @Expose()
  defectsComments?: string;

  @Expose()
  deviceId?: string;

  @Expose()
  dttm!: number;

  @Expose()
  createDttm!: number;

  @Expose()
  lastModifiedDttm!: number;

  @Expose()
  regionIds?: string[];

  @Expose()
  vehicleName?: string;

  @Expose()
  trailerName?: string;

  readonly entityType = 'DVIR';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for DVIREntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: DVIR): DVIREntity {
    return plainToInstance(DVIREntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<DVIR> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(DVIRCodec, object);
  }

  asTypeValue(): DVIR {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(DVIRCodec, object);
  }

  private countBaseTrue(keys: InspectionFieldKey[]): number {
    return keys
      .map((k) => this[k as keyof this] as unknown)
      .filter((v) => v === true)
      .length;
  }

  get vehicleBaseItemCount(): number {
    return DVIREntity.VEHICLE_INSPECTION_FIELDS.length;
  }

  get vehicleBaseDefectiveCount(): number {
    return this.countBaseTrue(DVIREntity.VEHICLE_INSPECTION_FIELDS as never);
  }

  get trailerBaseItemCount(): number {
    return DVIREntity.TRAILER_INSPECTION_FIELDS.length;
  }

  get trailerBaseDefectiveCount(): number {
    return this.countBaseTrue(DVIREntity.TRAILER_INSPECTION_FIELDS as never);
  }

  private get safeCustomValues(): DVIRCustomFieldValueEntity[] {
    return this.customFieldValues ?? [];
  }

  isCustomChecklistField(v: DVIRCustomFieldValueEntity): boolean {
    return v?.booleanValue !== undefined && v?.booleanValue !== null;
  }

  private isCustomDefect(v: DVIRCustomFieldValueEntity): boolean {
    return v?.booleanValue === true;
  }

  private countCustomItems(bucket: DVIRCustomFieldBucket): number {
    return this.safeCustomValues
      .filter((v) => this.isCustomChecklistField(v))
      .filter((v) => matchesDVIRCustomFieldBucket(bucket, v.customFieldDefCode))
      .length;
  }

  private countCustomDefects(bucket: DVIRCustomFieldBucket): number {
    return this.safeCustomValues
      .filter((v) => this.isCustomChecklistField(v))
      .filter((v) => matchesDVIRCustomFieldBucket(bucket, v.customFieldDefCode))
      .filter((v) => this.isCustomDefect(v))
      .length;
  }

  get vehicleItemCount(): number {
    return this.vehicleBaseItemCount + this.countCustomItems(DVIRCustomFieldBucket.VEHICLE);
  }

  get vehicleItemDefectiveCount(): number {
    return this.vehicleBaseDefectiveCount + this.countCustomDefects(DVIRCustomFieldBucket.VEHICLE);
  }

  get trailerItemCount(): number {
    return this.trailerBaseItemCount + this.countCustomItems(DVIRCustomFieldBucket.TRAILER);
  }

  get trailerItemDefectiveCount(): number {
    return this.trailerBaseDefectiveCount + this.countCustomDefects(DVIRCustomFieldBucket.TRAILER);
  }

  get itemCount(): number {
    return (
      this.vehicleItemCount +
      this.trailerItemCount
    );
  }

  get defectiveItemCount(): number {
    return (
      this.vehicleItemDefectiveCount +
      this.trailerItemDefectiveCount
    );
  }

  get requiresMechanicReview(): boolean {
    return !this.satisfactory
  }

  get isUnresolved(): boolean {
    return this.requiresMechanicReview && !this.defectsNeedNotCorrected && !this.defectsCorrected
  }

  get isPendingAck(): boolean {
    return this.requiresMechanicReview && !this.isUnresolved && this.person2Id == null
  }

  get status(): string {
    if (this.isUnresolved) {
      return 'Pending Mechanic Review'
    }

    if (this.isPendingAck) {
      return 'Pending Acknowledgement'
    }

    return "Resolved"
  }

  static VEHICLE_INSPECTION_FIELDS = [
    "vehicleAirCompressor",
    "vehicleAirLines",
    "vehicleBattery",
    "vehicleBrakes",
    "vehicleBrakeAccessories",
    "vehicleCarburetor",
    "vehicleClutch",
    "vehicleDefroster",
    "vehicleDriveLine",
    "vehicleEngine",
    "vehicleFifthWheel",
    "vehicleFrontAxle",
    "vehicleFuelTanks",
    "vehicleHeater",
    "vehicleHorn",
    "vehicleLights",
    "vehicleMirrors",
    "vehicleMuffler",
    "vehicleOilPressure",
    "vehicleOnBoardRecorder",
    "vehicleRadiator",
    "vehicleRearEnd",
    "vehicleReflectors",
    "vehicleSafetyEquipment",
    "vehicleSprings",
    "vehicleStarter",
    "vehicleSteering",
    "vehicleTachograph",
    "vehicleTires",
    "vehicleTransmission",
    "vehicleWheels",
    "vehicleWindows",
    "vehicleWindshieldWipers",
    "vehicleOther",
  ]

  static TRAILER_INSPECTION_FIELDS = [
    "trailerBrakeConnections",
    "trailerBrakes",
    "trailerCouplingChains",
    "trailerCouplingPin",
    "trailerDoors",
    "trailerHitch",
    "trailerLandingGear",
    "trailerLights",
    "trailerRoof",
    "trailerSprings",
    "trailerTarpaulin",
    "trailerTires",
    "trailerWheels",
    "trailerOther",
  ]
}

export type PartialDVIR = Partial<Omit<DVIR, 'customFieldValues'>> & {
  customFieldValues?: Partial<DVIRCustomFieldValue>[]
};

export const DVIRBadgeSummaryCodec = t.intersection([
  t.type({}),
  t.partial({
    createdPast7DaysCount: t.number,
    unresolvedCount: t.number,
    vehicleUnresolvedCount: t.number,
    trailerUnresolvedCount: t.number,
  }),
]);

export type DVIRBadgeSummary = t.TypeOf<typeof DVIRBadgeSummaryCodec>;

export interface DVIRSearchCriteria {
  fromDttm?: number;
  toDttm?: number;
  ids?: string[]
  region?: FieldMatch;
  shiftIds?: string[];
  createdBy?: FieldMatch;
  person?: FieldMatch;
  personSignedBy?: FieldMatch;
  personCombine?: MatchCombine
  assignedMechanic?: FieldMatch;
  reviewingMechanic?: FieldMatch;
  mechanicCombine?: MatchCombine
  vehicle?: FieldMatch;
  trailer?: FieldMatch;
  statuses?: string[]
  sort?: SortFieldSpec[]
  page?: PageSpec
}

export interface DVIRMechanicReview {
  mechanicSignatureBase64?: string;
  defectsCorrected?: boolean
  defectsNeedNotCorrected?: boolean
  defectsComments?: string
}
